import { useEffect, useState } from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { Meta } from '@blocks/api/types';

/**
 * Custom hook for making HTTP requests with optional aborting functionality.
 * @param {String} path - The URL path for the request.
 * @param {AxiosRequestConfig} config - Axios configuration object.
 * @param {Boolean} run - Whether to immediately execute the request.
 * @returns {Object} Response state and request control functions.
 */

type ResponseState<T = unknown> = {
  response: AxiosResponse<T> | undefined;
  error: AxiosError | undefined;
  isLoading: boolean;
  meta: Meta | undefined;
  body: T | null;
};

export default function useRequest<T>(
  path: string,
  config: AxiosRequestConfig = { headers: {} },
  run: boolean = true,
) {
  const [state, setState] = useState<ResponseState<T>>({
    response: undefined,
    error: undefined,
    isLoading: true,
    meta: undefined,
    body: null,
  });

  const serializedConfig = JSON.stringify(config);

  const request = () => {
    const controller = new AbortController();
    setState(prev => ({ ...prev, isLoading: true }));

    axios(path, {
      ...config,
      signal: controller.signal,
    })
      .then(response => {
        const {
          data: { body, meta },
        } = response;

        setState({
          error: undefined,
          response,
          body,
          meta,
          isLoading: false,
        });
      })
      .catch(error => {
        if (axios.isCancel(error) || error.name === 'CanceledError') {
          // request was aborted, do nothing
        } else {
          setState({
            error,
            response: undefined,
            isLoading: false,
            meta: undefined,
            body: null,
          });
        }
      });

    return controller;
  };

  useEffect(() => {
    let controller: AbortController | undefined;
    if (run) {
      controller = request();
    }

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [path, serializedConfig]);

  const { response, error, isLoading, meta, body } = state;

  const setData = (data: T) => {
    setState({ ...state, response: { ...response, data } });
  };

  return {
    meta: meta || undefined,
    body: body || undefined,
    response,
    error,
    isLoading,
    setData,
    request,
  };
}
